import React, { useMemo } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  Progress,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import FactorClassificationBadge from "./FactorClassificationBadge";
import { formatNumber } from "../networkReportFormatters";

const PREVIEW_LIMIT = 5;

export const selectLowestPerformingFactors = (factors = []) =>
  [...factors]
    .sort((left, right) => {
      const leftValue = left.stabilityIndex;
      const rightValue = right.stabilityIndex;
      const leftMissing = leftValue === null || leftValue === undefined;
      const rightMissing = rightValue === null || rightValue === undefined;

      if (leftMissing && rightMissing) return left.name.localeCompare(right.name);
      if (leftMissing) return 1;
      if (rightMissing) return -1;
      return leftValue - rightValue || left.name.localeCompare(right.name);
    })
    .slice(0, PREVIEW_LIMIT);

const LowestPerformingFactorsPreview = ({
  factors = [],
  loading = false,
  error = false,
  factorsPath,
}) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const trackColor = useColorModeValue("gray.100", "gray.700");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const previewFactors = useMemo(
    () => selectLowestPerformingFactors(factors),
    [factors]
  );
  const validValues = factors
    .map((factor) => factor.stabilityIndex)
    .filter((value) => Number.isFinite(value) && value >= 0);
  const scaleMaximum = Math.max(...validValues, 0);

  return (
    <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
      <CardBody>
        <Flex
          direction={{ base: "column", sm: "row" }}
          align={{ base: "stretch", sm: "center" }}
          justify="space-between"
          gap={3}
          mb={4}
        >
          <Box>
            <Heading size="sm">Lowest-performing factors</Heading>
            <Text mt={1} color={mutedColor} fontSize="sm">
              Ordered by network stability, lowest first.
            </Text>
          </Box>
          <Button
            as={RouterLink}
            to={factorsPath}
            size="sm"
            variant="outline"
            alignSelf={{ base: "stretch", sm: "center" }}
          >
            View full factor analysis
          </Button>
        </Flex>

        {loading ? (
          <Text color={mutedColor}>Loading factor preview...</Text>
        ) : error ? (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            Factor preview is temporarily unavailable.
          </Alert>
        ) : previewFactors.length === 0 ? (
          <Text color={mutedColor}>
            No factor data is available for this preview yet.
          </Text>
        ) : (
          <Stack spacing={4}>
            {previewFactors.map((factor, index) => {
              const hasValue = Number.isFinite(factor.stabilityIndex) &&
                factor.stabilityIndex >= 0;

              return (
                <Box key={factor.id}>
                  <Flex justify="space-between" align="start" gap={3}>
                    <HStack align="start" spacing={3} minW={0}>
                      <Text
                        aria-label={`Preview rank ${index + 1}`}
                        color={mutedColor}
                        fontWeight="semibold"
                        minW="1.25rem"
                      >
                        {index + 1}
                      </Text>
                      <Box minW={0}>
                        <Text fontWeight="semibold">{factor.name}</Text>
                        <Text color={mutedColor} fontSize="sm">
                          Factor category: {factor.areaLabel}
                        </Text>
                      </Box>
                    </HStack>
                    <Stack direction={{ base: "column", sm: "row" }} align="end" spacing={2}>
                      <FactorClassificationBadge classification={factor.classification} />
                      <Text fontWeight="semibold" whiteSpace="nowrap">
                        {formatNumber(factor.stabilityIndex)}
                      </Text>
                    </Stack>
                  </Flex>
                  <Progress
                    aria-label={`${factor.name} stability ${formatNumber(factor.stabilityIndex)}`}
                    value={hasValue ? factor.stabilityIndex : 0}
                    max={scaleMaximum || 1}
                    mt={2}
                    size="sm"
                    borderRadius="sm"
                    bg={trackColor}
                    colorScheme={
                      factor.classification === "strength"
                        ? "green"
                        : factor.classification === "development"
                          ? "orange"
                          : "gray"
                    }
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </CardBody>
    </Card>
  );
};

export default LowestPerformingFactorsPreview;
